import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { chatApi, ChatMessage, ChatSessionState } from '../services/api';
import chatbotIcon from '../assets/chatbot.png';

interface ChatPanelProps {
  sessionState: ChatSessionState;
  onSessionStateChange: (s: ChatSessionState) => void;
  onNavigate?: (step: string, resultData?: any) => void;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ sessionState, onSessionStateChange, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      const response = await chatApi.sendMessage(trimmed, messages, sessionState);
      onSessionStateChange(response.session_state);
      if (response.action === 'navigate' && response.action_data?.step && onNavigate) {
        const resultData: any = {};
        if (response.action_data.mode) resultData.mode = response.action_data.mode;
        if (response.action_data.algorithm) resultData.algorithm = response.action_data.algorithm;
        if (response.prediction_result) resultData.prediction_result = response.prediction_result;
        if (response.design_result) resultData.design_result = response.design_result;
        if (response.training_result) resultData.training_result = response.training_result;
        if (response.visualization) resultData.visualization = response.visualization;
        onNavigate(response.action_data.step, Object.keys(resultData).length > 0 ? resultData : undefined);
      }
      setMessages([...updatedMessages, { role: 'assistant', content: response.reply }]);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || 'Something went wrong. Please try again.';
      setMessages([...updatedMessages, { role: 'assistant', content: `Error: ${errorMsg}` }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        className="chat-toggle-btn"
        onClick={() => setIsOpen(true)}
        aria-label="Open chat"
      >
        <img src={chatbotIcon} alt="Chat" width="28" height="28" />
      </button>
    );
  }

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <div className="chat-header-title">
          <img src={chatbotIcon} alt="" width="20" height="20" />
          <span>Simerics AI Chat</span>
        </div>
        <button className="chat-close-btn" onClick={() => setIsOpen(false)} aria-label="Close chat">✕</button>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-welcome">
            <p>Hi! I can help you with predictions, design recommendations, training models, and more.</p>
            <p>Start by typing a message below.</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`chat-bubble chat-bubble-${msg.role}`}>
            <div className="chat-bubble-content">
              {msg.role === 'assistant' ? (
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="chat-bubble chat-bubble-assistant">
            <div className="chat-bubble-content chat-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <input
          ref={inputRef}
          type="text"
          className="chat-input"
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <button
          className="chat-send-btn"
          onClick={sendMessage}
          disabled={isLoading || !input.trim()}
          aria-label="Send message"
        >
          ➤
        </button>
      </div>
    </div>
  );
};
